// ─────────────────────────────────────────────────────────────────────────────
// İŞLEM GEÇMİŞİ — TÜRKÇE AÇIKLAMA ÜRETİCİ
//
// Alt admin / şube yöneticisi işlem loglarında ham API yolu yerine yöneticinin
// anlayacağı Türkçe açıklama üretir ("Fatura güncelleme", "Ürün ekleme" gibi).
//
// İki tüketicisi var:
//  - subAdminActivityLogger.ts: kayıt anında actionLabel olarak saklar,
//  - SubAdminActivityLogModal.tsx: eski/etiketsiz kayıtları ekranda çevirir.
//
// Ayrıca isReadOnlyApiPath ile POST olduğu halde veri DEĞİŞTİRMEYEN uçlar
// (sayfalı listeleme, rapor çalıştırma, sorgulama...) loglamadan tamamen
// dışlanır — yönetici loglarında yalnız gerçek yazma işlemleri görünür.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST ile çağrıldığı halde veri okuyan uçlar. Bu path'ler loglanmaz.
 * Segment sınırına dikkat: /check tam segment olmalı ki /checknotes (çek/senet)
 * gibi gerçek yazma uçları yanlışlıkla dışlanmasın; /list de /listings'i yakalamasın.
 */
const READ_ONLY_PATTERNS: RegExp[] = [
  /paged/,
  /\/list(\/|$)/,
  /search/,
  /query/,
  /filter/,
  /sorgula/, // ör. /api/einvoice/edm/mukellef-sorgula
  /lookup/,
  /preview/,
  /summary/,
  /\/stat(s|istics)(\/|$)/,
  /dynamicreport/, // /api/DynamicReport/run — rapor çalıştırma = okuma
  /report\/run/,
  /export/,
  /download/,
  /print/,
  /\/pdf(\/|$)/,
  /\/check(\/|$)/,
  /exists/,
  /calculate/,
  /\/count(\/|$)/,
  // Otomatik arka plan işleri — kullanıcı eylemi DEĞİL (ör. Getir sipariş durumu
  // senkronu her dakika POST atar; entegrasyon token yenileme de otomatiktir).
  /sync-status/,
  /refresh-token/,
  /heartbeat/,
  /\/ping(\/|$)/,
  /\/poll(\/|$)/,
];

export function isReadOnlyApiPath(path: string): boolean {
  const lower = (path || '').toLowerCase();
  return READ_ONLY_PATTERNS.some((re) => re.test(lower));
}

// ── Konu (hangi modül/kayıt türü) ────────────────────────────────────────────
// Sıra önemli: daha özgül önekler önce. Anahtar path'in İÇİNDE aranır
// (başlangıç değil) — bazı API'ler /api/hr/... gibi ara segment kullanıyor.
const SUBJECTS: Array<[string, string]> = [
  ['/personnel', 'Personel'],
  ['/permissions', 'Ekran yetkisi'],
  ['/shifts', 'Vardiya'],
  ['/leave', 'İzin'],
  ['/branch-personnel', 'Şube personel ataması'],
  ['/branches', 'Şube'],
  ['/products', 'Ürün'],
  // Bileşik Inventory uçları — genel '/inventory' (Stok) eşlemesinden ÖNCE gelmeli.
  ['/inventorybranchprice', 'Şube fiyatı'],
  ['/inventory/items', 'Ürün'],
  ['/inventory', 'Stok'],
  ['/categories', 'Kategori'],
  ['/warehouses', 'Depo'],
  ['/stock', 'Stok'],
  ['/invoices', 'Fatura'],
  ['/invoice', 'Fatura'],
  ['/einvoice', 'E-Fatura'],
  ['/accounts', 'Cari hesap'],
  ['/cashbox', 'Kasa'],
  ['/bankaccount', 'Banka hesabı'],
  ['/bankloan', 'Banka kredisi'],
  ['/banks', 'Banka'],
  ['/checknotes', 'Çek/Senet'],
  ['/checknote', 'Çek/Senet'],
  ['/offers', 'Teklif'],
  ['/offer', 'Teklif'],
  ['/orders', 'Sipariş'],
  ['/order', 'Sipariş'],
  ['/waybills', 'İrsaliye'],
  ['/waybill', 'İrsaliye'],
  ['/expenses', 'Gider'],
  ['/quicksale', 'Hızlı satış'],
  ['/packageorders', 'Paket sipariş'],
  ['/restaurant', 'Restoran'],
  ['/qrmenu', 'QR menü'],
  ['/kitchen', 'Mutfak ekranı'],
  ['/getir', 'Getir entegrasyonu'],
  ['/trendyol', 'Trendyol entegrasyonu'],
  ['/migros', 'Migros entegrasyonu'],
  ['/yemeksepeti', 'Yemeksepeti entegrasyonu'],
  ['/couriers', 'Kurye'],
  ['/courier', 'Kurye'],
  ['/tasks', 'Görev'],
  ['/task', 'Görev'],
  ['/reports', 'Rapor'],
  ['/notifications', 'Bildirim'],
  ['/surveys', 'Anket'],
  ['/survey', 'Anket'],
  ['/ecommerce', 'E-ticaret'],
  ['/wms', 'Depo yönetimi'],
  ['/ai', 'AI asistan'],
  ['/users', 'Kullanıcı'],
  ['/company', 'Firma'],
  ['/settings', 'Ayar'],
];

// ── Fiil (ne yaptı) ──────────────────────────────────────────────────────────
// Önce path içindeki eylem kelimelerine bakılır; bulunamazsa HTTP metodundan
// çıkarılır. (Bu projede güncelleme çoğunlukla ".../update" POST'u ile yapılır.)
const VERB_KEYWORDS: Array<[string, string]> = [
  ['update', 'güncelleme'],
  ['guncelle', 'güncelleme'],
  ['edit', 'güncelleme'],
  ['rename', 'ad değiştirme'],
  ['toggle', 'durum değiştirme'],
  ['status', 'durum değiştirme'],
  ['assign', 'atama'],
  ['approve', 'onaylama'],
  ['reject', 'reddetme'],
  ['cancel', 'iptal etme'],
  ['complete', 'tamamlama'],
  ['create', 'oluşturma'],
  ['olustur', 'oluşturma'],
  ['add', 'ekleme'],
  ['insert', 'ekleme'],
  ['register', 'kaydetme'],
  ['delete', 'silme'],
  ['remove', 'silme'],
  ['/sil', 'silme'],
  ['upload', 'dosya yükleme'],
  ['send', 'gönderme'],
  ['transfer', 'transfer'],
  ['payment', 'ödeme işlemi'],
  ['pay', 'ödeme işlemi'],
];

function resolveVerb(lowerPath: string, method: string): string {
  for (const [keyword, verb] of VERB_KEYWORDS) {
    if (lowerPath.includes(keyword)) return verb;
  }
  switch (method) {
    case 'PUT':
    case 'PATCH':
      return 'güncelleme';
    case 'DELETE':
      return 'silme';
    case 'POST':
    default:
      return 'işlemi';
  }
}

/**
 * path + method'dan Türkçe işlem açıklaması üretir.
 * Ör: ('/api/invoices/xxx/update', 'POST') → "Fatura güncelleme"
 * Konu bulunamazsa ham path döner (tooltip'te zaten teknik detay var).
 */
export function describeActivity(path: string, method: string): string {
  const lower = (path || '').toLowerCase();
  const upperMethod = (method || '').toUpperCase();

  let subject: string | undefined;
  for (const [keyword, label] of SUBJECTS) {
    if (lower.includes(keyword)) {
      subject = label;
      break;
    }
  }
  if (!subject) return path;

  const verb = resolveVerb(lower, upperMethod);
  return verb === 'işlemi' ? `${subject} işlemi` : `${subject} ${verb}`;
}
